import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Divider
} from "@mui/material";
import {
  Close,
  CheckBoxOutlineBlank,
  Business,
  DateRange,
  FormatListNumbered
} from "@mui/icons-material";
import CustomButton from "../../shared/button/CustomButton";
import { useFetchTemplateChecklistItemsByChecklistId } from "../../../hooks/api/templateChecklistApiHooks";
import { formatDate } from "../../../services/date";

export default function TemplateChecklistViewer({ open, onClose, template }) {
  const { templateChecklistItemsLoading, templateChecklistItemsData } = 
    useFetchTemplateChecklistItemsByChecklistId(template?.id);

  if (!template) {
    return null;
  }

  const sortedItems = templateChecklistItemsData 
    ? [...templateChecklistItemsData].sort((a, b) => a.order - b.order)
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {template.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Business fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {template.powerDistributionCompany?.name || 'Concessionária não encontrada'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DateRange fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Criado em {formatDate(template.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {templateChecklistItemsLoading && <LinearProgress />}

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              icon={<FormatListNumbered />}
              label={`${sortedItems.length} ${sortedItems.length === 1 ? 'item' : 'itens'}`}
              variant="outlined"
              color="primary"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Este template será usado como base para criar checklists em novos projetos 
            desta concessionária.
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {templateChecklistItemsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Carregando itens do template...
            </Typography>
          </Box>
        ) : sortedItems.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 6,
              flexDirection: 'column'
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Nenhum item encontrado
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Este template ainda não possui itens configurados
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {sortedItems.map((item, index) => (
              <ListItem
                key={item.id}
                sx={{
                  py: 1.5,
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckBoxOutlineBlank color="action" />
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Typography variant="body1">
                      {item.content}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Item {index + 1} • Ordem: {item.order}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Visualização do template • Somente leitura
          </Typography>
          
          <CustomButton
            buttonText="Fechar"
            handleButtonClick={onClose}
            variant="contained"
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}